import { Fragment } from 'react';
import { t } from '@lingui/core/macro';
import { useEsigning } from 'app/domains/esigning/components/EsigningProvider';
import { CreateSignRequestRejectionReason } from 'shared/domains/apollo/generated/types';
import { BasicAlert } from './BasicAlert';
import { LatestAttemptFailed } from './LatestAttemptFailed';
import { NoApprovalError } from './NoApprovalError';
import { CreateSignRequestErrors, CreateSignRequestFailedSubcodes } from '../../../EsigningProvider/types';
export function ErrorAlert() {
    const { failed, submittingCreation, createSignRequestError, createSignRequestFailedSubcode, createSignRequestRejectionReasons, lastSignRequestAttemptTimestamp, createSignRequestSubmitCount, } = useEsigning();
    const getMessageFromReason = (reason) => {
        switch (reason) {
            case CreateSignRequestRejectionReason.ActiveSignRequest:
                return t({
                    comment: 'Send document for eSigning error - Active sign request',
                    message: 'The document is already sent for signing, please reload the page',
                });
            case CreateSignRequestRejectionReason.InvalidOrder:
                return t({
                    comment: 'Send document for eSigning error - Invalid order',
                    message: 'The signing order is invalid, please change it and try again',
                });
            case CreateSignRequestRejectionReason.MissingMobileNumber:
                return t({
                    comment: 'Send document for eSigning error - Missing mobile number',
                    message: 'A signing party is missing a mobile number, please add it and try again',
                });
            case CreateSignRequestRejectionReason.MissingSignatories:
                return t({
                    comment: 'Send document for eSigning error - Missing signatories',
                    message: 'You must add at least one signing party',
                });
            case CreateSignRequestRejectionReason.NotConnectedToEsigningProvider:
                return t({
                    comment: 'Send document for eSigning error - Not connected to eSigning provider',
                    message: 'Your company is not connected to an eSigning provider. Connect to one and try again.',
                });
            case CreateSignRequestRejectionReason.NotEnoughCredits:
                return t({
                    comment: 'Send document for eSigning error - Not enough credits',
                    message: 'You have insufficient credits on your account to do this action',
                });
            case CreateSignRequestRejectionReason.UnsupportedAuthenticationMode:
            case CreateSignRequestRejectionReason.ProviderExpectsFirstNameLastName:
            case CreateSignRequestRejectionReason.ProviderExpectsName:
            case CreateSignRequestRejectionReason.NotAuthorized:
            case CreateSignRequestRejectionReason.MissingPdfFile:
            case CreateSignRequestRejectionReason.Failed:
                return t({
                    comment: 'Send document for eSigning error - Failed',
                    message: 'The eSigning has failed, please try again',
                });
            default:
                return '';
        }
    };
    if (createSignRequestRejectionReasons.length > 0) {
        return (<Fragment>
        {createSignRequestRejectionReasons.map((reason) => (<BasicAlert key={reason} message={getMessageFromReason(reason)}/>))}
      </Fragment>);
    }
    if (createSignRequestFailedSubcode) {
        switch (createSignRequestFailedSubcode) {
            case CreateSignRequestFailedSubcodes.AttachmentUploadFailed:
                return (<BasicAlert message={t({
                        comment: 'The upload failed for the attachment - generic error message',
                        message: 'Failed to upload one or more attachments, please try again',
                    })}/>);
            case CreateSignRequestFailedSubcodes.AttachmentUploadFailedInvalidPdf:
                return (<BasicAlert message={t({
                        comment: 'The upload failed for the attachment - invalid pdf message',
                        message: 'One or more attachments cannot be processed because they are protected. Please ensure your PDF attachments are unprotected and try again.',
                    })}/>);
            default:
                return null;
        }
    }
    // The latest sign request attempt failed but the user has reloaded the app since
    if (createSignRequestSubmitCount === 0 && failed) {
        return <LatestAttemptFailed latestAttemptTimestamp={lastSignRequestAttemptTimestamp}/>;
    }
    if (createSignRequestSubmitCount === 0 || submittingCreation) {
        return null;
    }
    if (failed) {
        return (<BasicAlert message={t({
                message: 'The e-signing has failed. Please try again.',
                comment: 'Error message when e-signing has failed and goes back to the form',
            })}/>);
    }
    switch (createSignRequestError) {
        case CreateSignRequestErrors.NoCredits:
            return (<BasicAlert message={t({
                    message: 'You have insufficient credits on your account to do this action',
                    comment: 'Error message for when user tries to send out document for signing when the company has 0 credits',
                })}/>);
        case CreateSignRequestErrors.NoSignatory:
            return (<BasicAlert message={t({
                    message: 'You must add at least one signing party',
                    comment: 'Error message for when a user tries to send out document for signing with no parties',
                })}/>);
        case CreateSignRequestErrors.NoScriveToken:
            return (<BasicAlert message={t({
                    message: 'You have insufficient credits on your account to do this action. Please contact Scrive for troubleshooting',
                    comment: 'Error message for when user tries to send out document for signing when no scrive tokens',
                })}/>);
        case CreateSignRequestErrors.NoApproval:
            return <NoApprovalError />;
        case CreateSignRequestErrors.NoPdfConversion:
            return (<BasicAlert message={t({
                    message: 'Failed to convert document to pdf for e-signing',
                    comment: 'Error message for when we fail to convert the document to pdf for e-signing',
                })}/>);
        case CreateSignRequestErrors.UnexpectedError:
            return (<BasicAlert message={t({
                    message: 'Something went wrong, please try again later',
                    comment: 'Unexpected error happened',
                })}/>);
        default:
            return null;
    }
}
